import apiClient from '@src/api/clients';

const BASE_URL = 'creative-studio/';

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );

const listResource = (resource, params = {}) =>
  apiClient.request({
    url: `${BASE_URL}${resource}/`,
    method: 'GET',
    payload: cleanParams(params),
  });

const createResource = (resource, payload, options = {}) =>
  apiClient.request({
    url: `${BASE_URL}${resource}/`,
    method: 'POST',
    payload,
    multipart: options.multipart || false,
  });

const updateResource = (resource, id, payload, options = {}) =>
  apiClient.request({
    url: `${BASE_URL}${resource}/${id}/`,
    method: 'PATCH',
    payload,
    multipart: options.multipart || false,
  });

const renderAdVariant = (id) =>
  apiClient.request({
    url: `${BASE_URL}ad-variants/${id}/render/`,
    method: 'POST',
    payload: {},
  });

export const listHookTemplates = (params = {}) => listResource('hook-templates', params);
export const createHookTemplate = (payload) => createResource('hook-templates', payload);
export const updateHookTemplate = (id, payload) => updateResource('hook-templates', id, payload);

export const listHookAssets = (params = {}) => listResource('hook-assets', params);
export const createHookAsset = (payload) => createResource('hook-assets', payload, { multipart: true });

export const listBodyVideoAssets = (params = {}) => listResource('body-video-assets', params);
export const createBodyVideoAsset = (payload) => createResource('body-video-assets', payload, { multipart: true });

export const listCTAAssets = (params = {}) => listResource('cta-assets', params);
export const createCTAAsset = (payload) => createResource('cta-assets', payload, { multipart: true });

export const listVoiceoverAssets = (params = {}) => listResource('voiceover-assets', params);
export const createVoiceoverAsset = (payload) => createResource('voiceover-assets', payload, { multipart: true });

export const listAdVariants = (params = {}) => listResource('ad-variants', params);
export const createAdVariant = (payload) => createResource('ad-variants', payload, { multipart: true });
export const updateAdVariant = (id, payload) => updateResource('ad-variants', id, payload, { multipart: true });

export const listRenderJobs = (params = {}) => listResource('render-jobs', params);

// ---------------------------------------------------------------------------
// Creative Task Workflow
// ---------------------------------------------------------------------------

export const listAvatars = (params = {}) => listResource('avatars', params);
export const createAvatar = (payload) => createResource('avatars', payload);
export const updateAvatar = (id, payload) => updateResource('avatars', id, payload);

export const listPainPoints = (params = {}) => listResource('pain-points', params);
export const createPainPoint = (payload) => createResource('pain-points', payload);
export const updatePainPoint = (id, payload) => updateResource('pain-points', id, payload);

export const listAngleChoices = (params = {}) => listResource('angle-choices', params);
export const createAngleChoice = (payload) => createResource('angle-choices', payload);
export const updateAngleChoice = (id, payload) => updateResource('angle-choices', id, payload);
export const listHookTypeChoices = (params = {}) => listResource('hook-type-choices', params);
export const createHookTypeChoice = (payload) => createResource('hook-type-choices', payload);
export const updateHookTypeChoice = (id, payload) => updateResource('hook-type-choices', id, payload);
export const listStyleChoices = (params = {}) => listResource('style-choices', params);
export const createStyleChoice = (payload) => createResource('style-choices', payload);
export const updateStyleChoice = (id, payload) => updateResource('style-choices', id, payload);
export const listAdTypeChoices = (params = {}) => listResource('ad-type-choices', params);
export const createAdTypeChoice = (payload) => createResource('ad-type-choices', payload);
export const updateAdTypeChoice = (id, payload) => updateResource('ad-type-choices', id, payload);
export const listDesignerProfiles = (params = {}) => listResource('designer-profiles', params);
export const createDesignerProfile = (payload) => createResource('designer-profiles', payload);
export const updateDesignerProfile = (id, payload) => updateResource('designer-profiles', id, payload);
export const listDesignerGroups = (params = {}) => listResource('designer-groups', params);
export const createDesignerGroup = (payload) => createResource('designer-groups', payload);
export const updateDesignerGroup = (id, payload) => updateResource('designer-groups', id, payload);

// Client verticals (from creative knowledge)
export const fetchClientVerticals = (clientId) =>
  apiClient.request({
    url: `dashboard/client-creative-knowledge/`,
    method: 'GET',
    payload: { client_id: clientId },
  });

// Flights
export const listFlights = (params = {}) => listResource('flights', params);
export const fetchFlight = (id) =>
  apiClient.request({ url: `${BASE_URL}flights/${id}/`, method: 'GET' });
export const createFlight = (payload) => createResource('flights', payload);
export const updateFlight = (id, payload) => updateResource('flights', id, payload);
export const deleteFlight = (id) =>
  apiClient.request({ url: `${BASE_URL}flights/${id}/`, method: 'DELETE' });
export const generateFlightTasks = (flightId, payload) =>
  apiClient.request({ url: `${BASE_URL}flights/${flightId}/generate-tasks/`, method: 'POST', payload });
export const autoSelectFlight = (flightId, payload) =>
  apiClient.request({ url: `${BASE_URL}flights/${flightId}/auto-select/`, method: 'POST', payload });
export const archiveFlight = (flightId) =>
  apiClient.request({ url: `${BASE_URL}flights/${flightId}/archive/`, method: 'POST' });
export const exportFlightTasks = (flightId) =>
  apiClient.request({ url: `${BASE_URL}flights/${flightId}/export/`, method: 'GET', options: { responseType: 'blob' } });

// Designer API
export const fetchDesignerAPI = (params = {}) => listResource('designer-api', params);
export const fetchDesignerAPIMe = () =>
  apiClient.request({ url: `${BASE_URL}designer-api/me/`, method: 'GET' });
export const getDesignerAPIToken = () =>
  apiClient.request({ url: `${BASE_URL}designer-api/token/`, method: 'POST' });
export const regenerateDesignerAPIToken = () =>
  apiClient.request({ url: `${BASE_URL}designer-api/token/regenerate/`, method: 'POST' });

// Tasks
export const listCreativeTasks = (params = {}) => listResource('tasks', params);
export const bulkUpdateTasks = (payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/bulk-update/`, method: 'POST', payload });
export const generateScript = (taskId, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/generate-script/`, method: 'POST', payload });

export const generateScriptVariants = (taskId, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/generate-variants/`, method: 'POST', payload });

export const rankScriptVariants = (taskId, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/rank-variants/`, method: 'POST', payload });

export const listTaskScripts = (taskId) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/scripts/`, method: 'GET' });
export const thumbTaskScript = (taskId, scriptId, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/scripts/${scriptId}/thumb/`, method: 'POST', payload });
export const selectTaskScript = (taskId, scriptId) =>
  apiClient.request({ url: `${BASE_URL}tasks/${taskId}/scripts/${scriptId}/select/`, method: 'POST', payload: {} });

// Script Guardrails
export const listScriptGuardrails = (params = {}) => listResource('script-guardrails', params);
export const createScriptGuardrail = (payload) => createResource('script-guardrails', payload);
export const updateScriptGuardrail = (id, payload) => updateResource('script-guardrails', id, payload);

// Script Prompt Templates
export const listScriptPrompts = (params = {}) => listResource('script-prompts', params);
export const updateScriptPrompt = (id, payload) => updateResource('script-prompts', id, payload);
export const listSelectionPrompts = (params = {}) => listResource('selection-prompts', params);
export const updateSelectionPrompt = (id, payload) => updateResource('selection-prompts', id, payload);

export const fetchCreativeTask = (id) =>
  apiClient.request({
    url: `${BASE_URL}tasks/${id}/`,
    method: 'GET',
  });

export const createCreativeTask = (payload) => createResource('tasks', payload);
export const updateCreativeTask = (id, payload) => updateResource('tasks', id, payload);

export const deleteCreativeTask = (id) =>
  apiClient.request({
    url: `${BASE_URL}tasks/${id}/`,
    method: 'DELETE',
  });

export const assignCreativeTask = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/assign/`, method: 'POST', payload: {} });

export const startCreativeTask = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/start/`, method: 'POST', payload: {} });

export const submitCreativeTask = (id, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/submit/`, method: 'POST', payload });

export const requestRevisionCreativeTask = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/request-revision/`, method: 'POST', payload });

export const approveCreativeTask = (id, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/approve/`, method: 'POST', payload });

export const rateCreativeTask = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/rate/`, method: 'POST', payload });

export const fetchCreativeTaskComments = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/comments/`, method: 'GET' });

export const addCreativeTaskComment = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/comments/`, method: 'POST', payload });

export const uploadCreativeTaskFile = (id, formData) =>
  apiClient.request({
    url: `${BASE_URL}tasks/${id}/upload/`,
    method: 'POST',
    payload: formData,
    multipart: true,
  });

export const generateAdCopy = (id, payload = {}) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/generate-ad-copy/`, method: 'POST', payload });

export const listAdCopyPrompts = (params = {}) => listResource('ad-copy-prompts', params);
export const updateAdCopyPrompt = (id, payload) => updateResource('ad-copy-prompts', id, payload);

export const fetchLaunchPrefill = (id) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/launch-prefill/`, method: 'GET' });

export const launchTaskToFacebook = (id, payload) =>
  apiClient.request({ url: `${BASE_URL}tasks/${id}/launch-ad/`, method: 'POST', payload });

export const fetchFacebookAdStructure = (clientId, includeInactive = false) =>
  apiClient.request({
    url: `dashboard/facebook/ad-structure/`,
    method: 'GET',
    payload: { client_id: clientId, include_inactive: includeInactive },
  });

export const fetchFacebookAdStructureDB = (clientId) =>
  apiClient.request({
    url: `dashboard/facebook/ad-structure-db/`,
    method: 'GET',
    payload: { client_id: clientId },
  });

// ---------------------------------------------------------------------------
// Concept × Cell System (v1) — Phase 4 API
// ---------------------------------------------------------------------------

export const fetchActiveConstitution = () =>
  apiClient.request({
    url: `${BASE_URL}constitutions/active/`,
    method: 'GET',
    payload: {},
  });

export const updateConstitution = (id, payload) => updateResource('constitutions', id, payload);

export const listAdConcepts = (params = {}) => listResource('ad-concepts', params);

export const fetchAdConcept = (slug) =>
  apiClient.request({
    url: `${BASE_URL}ad-concepts/${slug}/`,
    method: 'GET',
    payload: {},
  });

export const listSubAxisCells = (params = {}) => listResource('sub-axis-cells', params);

export const listWinnerLibrary = (params = {}) => listResource('winner-library', params);
export const createWinnerLibraryEntry = (payload) => createResource('winner-library', payload);
export const updateWinnerLibraryEntry = (id, payload) => updateResource('winner-library', id, payload);
export const deleteWinnerLibraryEntry = (id) =>
  apiClient.request({ url: `${BASE_URL}winner-library/${id}/`, method: 'DELETE', payload: {} });

export const addBangerFromDecomposition = (payload) =>
  apiClient.request({
    url: `${BASE_URL}winner-library/add-banger/`,
    method: 'POST',
    payload,
  });

// Ad Inspiration — paste an IG/FB ad link (or upload a file); a background
// job fetches the video and decomposes it with Gemini.
export const listAdInspirations = (params = {}) => listResource('ad-inspirations', params);

export const fetchAdInspiration = (id) =>
  apiClient.request({ url: `${BASE_URL}ad-inspirations/${id}/`, method: 'GET', payload: {} });

export const createAdInspiration = ({ sourceUrl, title, file } = {}) => {
  const fd = new FormData();
  if (sourceUrl) fd.append('source_url', sourceUrl);
  if (title) fd.append('title', title);
  if (file) fd.append('video', file);
  return apiClient.request({
    url: `${BASE_URL}ad-inspirations/`,
    method: 'POST',
    payload: fd,
    multipart: true,
  });
};

export const reanalyzeAdInspiration = (id) =>
  apiClient.request({
    url: `${BASE_URL}ad-inspirations/${id}/reanalyze/`,
    method: 'POST',
    payload: {},
  });

export const listLaneBriefs = (params = {}) => listResource('lane-briefs', params);
export const fetchLaneBrief = (id) =>
  apiClient.request({ url: `${BASE_URL}lane-briefs/${id}/`, method: 'GET', payload: {} });
export const updateLaneBrief = (id, payload) => updateResource('lane-briefs', id, payload);
export const deleteLaneBrief = (id) =>
  apiClient.request({ url: `${BASE_URL}lane-briefs/${id}/`, method: 'DELETE', payload: {} });

export const generateFlight = (payload) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/generate-flight/`,
    method: 'POST',
    payload,
  });

// Generate ONE brief from a specific cell (bypasses the auto-composer).
export const generateBriefFromCell = ({ cellId, laneId }) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/generate-from-cell/`,
    method: 'POST',
    payload: { cell_id: cellId, lane_id: laneId },
  });

// Create a designer-authored brief (no AI). The handwritten markdown is
// stored as rendered_brief and the brief lands status=APPROVED.
export const createManualBrief = (payload) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/manual/`,
    method: 'POST',
    payload,
  });

export const rejudgeLaneBrief = (id) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${id}/rejudge/`,
    method: 'POST',
    payload: {},
  });

export const markLaneBriefLaunched = (id) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${id}/mark-launched/`,
    method: 'POST',
    payload: {},
  });

// Hand a brief to a designer — bridges it into the concept_cell
// CreativeFlight/CreativeTask workflow and moves the task to ASSIGNED.
// payload: { designerId } OR { designerGroupId } (exactly one), optional flightId.
export const assignLaneBrief = (id, { designerId, designerGroupId, flightId } = {}) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${id}/assign/`,
    method: 'POST',
    payload: {
      ...(designerId ? { designer_id: designerId } : {}),
      ...(designerGroupId ? { designer_group_id: designerGroupId } : {}),
      ...(flightId ? { flight_id: flightId } : {}),
    },
  });

export const unassignLaneBrief = (id) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${id}/unassign/`,
    method: 'POST',
    payload: {},
  });

// Fan an approved lane master to its lane's clients. GET = preview (no
// writes), POST = commit (creates LaneCreativeBriefDistribution rows).
export const deployLaneBrief = (
  id,
  { commit = false, weekStart = '', designerId = '', designerGroupId = '' } = {},
) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${id}/deploy/`,
    method: commit ? 'POST' : 'GET',
    payload: {
      ...(weekStart ? { week_start: weekStart } : {}),
      ...(designerId ? { designer_id: designerId } : {}),
      ...(designerGroupId ? { designer_group_id: designerGroupId } : {}),
    },
  });

export const listLaneBriefDistributions = (params = {}) =>
  listResource('lane-brief-distributions', params);

export const proposeCells = (conceptSlug, payload) =>
  apiClient.request({
    url: `${BASE_URL}ad-concepts/${conceptSlug}/propose-cells/`,
    method: 'POST',
    payload,
  });

export const acceptCellProposal = (conceptSlug, proposal) =>
  apiClient.request({
    url: `${BASE_URL}ad-concepts/${conceptSlug}/accept-cell-proposal/`,
    method: 'POST',
    payload: proposal,
  });

export const auditConstitution = (constitutionId, payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}constitutions/${constitutionId}/audit/`,
    method: 'POST',
    payload,
  });

export const applyConstitutionEdit = (constitutionId, payload) =>
  apiClient.request({
    url: `${BASE_URL}constitutions/${constitutionId}/apply-edit/`,
    method: 'POST',
    payload,
  });

export const renderLaneBrief = (briefId, { tier = 'fast', lipsync = false, captionsMode = 'auto' } = {}) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${briefId}/render/`,
    method: 'POST',
    payload: { tier, lipsync, captions_mode: captionsMode },
  });

export const listRenderedAssets = (briefId) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${briefId}/rendered-assets/`,
    method: 'GET',
    payload: {},
  });

export const recomposeLaneBrief = (briefId, { sourceAssetId, regenerateShotIds = [], tier = 'fast', captionsMode = 'auto' }) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${briefId}/recompose/`,
    method: 'POST',
    payload: { source_asset_id: sourceAssetId, regenerate_shot_ids: regenerateShotIds, tier, captions_mode: captionsMode },
  });

// Shot Builder — per-shot human override (AI default | uploaded clip | generic).
export const listBriefShots = (briefId) =>
  apiClient.request({
    url: `${BASE_URL}lane-briefs/${briefId}/shots/`,
    method: 'GET',
    payload: {},
  });

export const setBriefShotSource = (briefId, { shotId, mode, file, note = '' }) => {
  const fd = new FormData();
  fd.append('shot_id', shotId);
  fd.append('mode', mode);
  if (note) fd.append('note', note);
  if (file) fd.append('file', file);
  return apiClient.request({
    url: `${BASE_URL}lane-briefs/${briefId}/shot-source/`,
    method: 'POST',
    payload: fd,
    multipart: true,
  });
};

// Creative Demand board (Phase 4) — the demand brain that ranks where to produce.
export const fetchCreativeDemand = (params = {}) =>
  apiClient.request({
    url: `${BASE_URL}creative-demand/latest/`,
    method: 'GET',
    payload: cleanParams(params),
  });

export const recomputeCreativeDemand = (payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}creative-demand/recompute/`,
    method: 'POST',
    payload,
  });

// Phase 5 — seed a flight (generate briefs) for a lane straight from the board.
export const seedFlightFromDemand = (payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}creative-demand/seed-flight/`,
    method: 'POST',
    payload,
  });

// Phase 5 — preview how a client selection maps to flights (no generation).
export const planSeedFlights = (payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}creative-demand/plan-seed/`,
    method: 'POST',
    payload,
  });

// Phase 5 — generate flights for a client selection (one per lane + solo).
export const seedFlightsForSelection = (payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}creative-demand/seed-flights/`,
    method: 'POST',
    payload,
  });

// Phase 5 — bulk plan: propose a depth-weighted lane plan for a target ad count.
export const bulkPlanDemand = (payload = {}) =>
  apiClient.request({
    url: `${BASE_URL}creative-demand/bulk-plan/`,
    method: 'POST',
    payload,
  });

export default {
  fetchCreativeDemand,
  recomputeCreativeDemand,
  seedFlightFromDemand,
  planSeedFlights,
  seedFlightsForSelection,
  bulkPlanDemand,
  listHookTemplates,
  createHookTemplate,
  updateHookTemplate,
  listHookAssets,
  createHookAsset,
  listBodyVideoAssets,
  createBodyVideoAsset,
  listCTAAssets,
  createCTAAsset,
  listVoiceoverAssets,
  createVoiceoverAsset,
  listAdVariants,
  createAdVariant,
  updateAdVariant,
  listRenderJobs,
  renderAdVariant,
  listAvatars,
  createAvatar,
  updateAvatar,
  listPainPoints,
  createPainPoint,
  updatePainPoint,
  listAngleChoices,
  createAngleChoice,
  updateAngleChoice,
  listStyleChoices,
  createStyleChoice,
  updateStyleChoice,
  listDesignerProfiles,
  createDesignerProfile,
  updateDesignerProfile,
  listCreativeTasks,
  fetchCreativeTask,
  createCreativeTask,
  updateCreativeTask,
  deleteCreativeTask,
  startCreativeTask,
  submitCreativeTask,
  requestRevisionCreativeTask,
  approveCreativeTask,
  fetchCreativeTaskComments,
  addCreativeTaskComment,
  uploadCreativeTaskFile,
  generateScript,
  generateScriptVariants,
  rankScriptVariants,
  listTaskScripts,
  thumbTaskScript,
  selectTaskScript,
  // Concept × Cell system
  fetchActiveConstitution,
  updateConstitution,
  listAdConcepts,
  fetchAdConcept,
  listSubAxisCells,
  listWinnerLibrary,
  createWinnerLibraryEntry,
  updateWinnerLibraryEntry,
  deleteWinnerLibraryEntry,
  addBangerFromDecomposition,
  listAdInspirations,
  fetchAdInspiration,
  createAdInspiration,
  reanalyzeAdInspiration,
  listLaneBriefs,
  fetchLaneBrief,
  updateLaneBrief,
  deleteLaneBrief,
  generateFlight,
  generateBriefFromCell,
  createManualBrief,
  rejudgeLaneBrief,
  markLaneBriefLaunched,
  assignLaneBrief,
  unassignLaneBrief,
  deployLaneBrief,
  listBriefShots,
  setBriefShotSource,
  listLaneBriefDistributions,
  proposeCells,
  acceptCellProposal,
  auditConstitution,
  applyConstitutionEdit,
  renderLaneBrief,
  listRenderedAssets,
  recomposeLaneBrief,
};
